import {
	Dispatch,
	SetStateAction,
	KeyboardEvent,
	useCallback,
	ChangeEvent,
} from 'react';
import classNames from 'classnames';
import { ReactComponent as UpIcon } from './assets/up.svg';
import { ReactComponent as ClearIcon } from './assets/clearBtn.svg';
import st from './InputNumber.module.scss';

interface IInputNumberProps extends React.HTMLAttributes<HTMLDivElement> {
	readOnly?: boolean;
	size?: 'min' | 'std' | 'large';
	isClearBtn?: boolean;
	value?: number | null;
	placeholder?: string;
	mode?: keyof typeof Modes;
	min?: number;
	max?: number;
	step?: number;
	isInvalid?: boolean;
	rlt?: boolean;
	onChangeValue: Dispatch<SetStateAction<number | null>>;
}

enum Modes {
	natural = 'natural',
	integer = 'integer',
	floating = 'floating',
	scientific = 'scientific',
}

const patternMapping = {
	[Modes.natural]: '(?:0|[1-9]\\d*)',
	[Modes.integer]: '[+\\-]?(?:0|[1-9]\\d*)',
	[Modes.floating]: '[+\\-]?(?:0|[1-9]\\d*)(?:\\.\\d+)?',
	[Modes.scientific]:
		'[+\\-]?(?:0|[1-9]\\d*)(?:\\.\\d+)?(?:[eE][+\\-]?\\d+)?',
};

const InputNumber: React.FC<IInputNumberProps> = (props) => {
	const {
		value,
		className,
		readOnly = false,
		step = 1,
		min = -Infinity,
		max = Infinity,
		isClearBtn = true,
		placeholder = '',
		rlt = false,
		isInvalid = false,
		mode = Modes.natural,
		size = 'std',
		onChangeValue,
		...otherProps
	} = props;

	const handleChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const _val = event.target.value.replace(/\D/g, '');
			if (_val === null) {
				onChangeValue(null);
			} else {
				const val = Number.parseInt(_val);
				if (!Number.isNaN(val)) {
					onChangeValue(val);
				}
			}
		},
		[onChangeValue],
	);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			const inputValue = (event.target as HTMLInputElement).value;
			// handle increment
			if (event.key === 'ArrowUp') {
				// empty input value has to be normalized to zero
				const nextValue = Number(inputValue || 0) + step;
				if (nextValue <= max && onChangeValue) {
					onChangeValue(nextValue);
				}
			}
			// handle decrement
			if (event.key === 'ArrowDown') {
				const nextValue = Number(inputValue || 0) - step;
				if (nextValue >= min && onChangeValue) {
					onChangeValue(nextValue ?? 0);
				}
			}
		},
		[max, min, onChangeValue, step],
	);

	const onChangeValueHandler = (step: number) => {
		const newValue = +(value ?? 0) + step;
		if (newValue >= min && newValue <= max) {
			if (onChangeValue) {
				onChangeValue(newValue);
			}
		}
	};

	const onClearHandler = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		e.stopPropagation();
		if (!!onChangeValue && !readOnly) {
			onChangeValue(null);
		}
	};

	const classes = classNames(st.control, className, st[size], {
		[st.disabled]: !!readOnly,
		[st.invalidate]: isInvalid,
	});

	return (
		<div className={classes}>
			<div className={st.buttons}>
				<button
					type="button"
					onClick={() => {
						return onChangeValueHandler(1);
					}}
					disabled={readOnly}
				>
					<UpIcon />
				</button>
				<button
					type="button"
					onClick={() => {
						return onChangeValueHandler(-1);
					}}
					disabled={readOnly}
				>
					<UpIcon />
				</button>
			</div>
			<input
				{...otherProps}
				type="text"
				dir={rlt ? 'rtl' : ''}
				inputMode="numeric"
				pattern={patternMapping[mode]}
				placeholder={placeholder}
				disabled={readOnly}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				value={value ?? 0}
			/>
			{isClearBtn && readOnly && (
				<button
					type="button"
					disabled={!value}
					onClick={onClearHandler}
				>
					<ClearIcon />
				</button>
			)}
		</div>
	);
};

export default InputNumber;
